import { createContext, ReactNode, useEffect, useReducer } from "react";
// utils
import { isValidToken, setSession } from "../utils/jwt";

// @types
import {
  ActionMap,
  AuthState,
  AuthUser,
  JWTContextType,
} from "../@types/authentication";
import baseService from "../services/base.service";

// ----------------------------------------------------------------------

enum Types {
  Initial = "INITIALIZE",
  Login = "LOGIN",
  Logout = "LOGOUT",
  Register = "REGISTER",
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
    isLoading: boolean;
    };
  [Types.Login]: {
    user: AuthUser;
    isLoading: boolean;
  };
  [Types.Logout]: {
    isLoading: boolean;
  };
  [Types.Register]: {
    user: AuthUser;
    isLoading: boolean;
  };
};

export type JWTActions =
  ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  isLoading: false,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case "INITIALIZE":
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
        isLoading: action.payload.isLoading,
      };
    case "LOGIN":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isLoading: action.payload.isLoading,
      };
    case "LOGOUT":
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        isLoading: action.payload.isLoading,
      };

    case "REGISTER":
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        isLoading: action.payload.isLoading,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          const user = await getUserInfo();

          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user,
              isLoading: false,
            },
          });
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
              isLoading: false,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
            isLoading: false,
          },
        });
      }
    };

    initialize();
  }, []);

  const setUserInfo = async (userInfo: any) => {
    dispatch({
      type: Types.Initial,
      payload: {
        isAuthenticated: true,
        user: userInfo,
        isLoading: false,
      },
    });
  }

  const getUserInfo = async () => {
    const user_response = await baseService.post("/user/info");
    return user_response?.data;
  };

  // const login = async (code: string) => {
  //   const login_response = await baseService.post(`/user/login?code=${code}`);
  //   console.log(login_response);
  //   if (login_response?.status === 200) {
  //     const { token } = await login_response?.data?.data;
  //     const accessToken = token?.access_token;
  //     setSession(accessToken);
  //     const user = await getUserInfo();
  //     dispatch({
  //       type: Types.Login,
  //       payload: {
  //         user,
  //       },
  //     });
  //   } 
  // };

    const login = async () => {
      const user = await getUserInfo();
      dispatch({
        type: Types.Login,
        payload: {
          user,
          isLoading: false,
        },
      });
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => {
    const response = await baseService.post("/api/account/register", {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem("accessToken", accessToken);
    dispatch({
      type: Types.Register,
      payload: {
        user,
        isLoading: false,
      },
    });
  };

  const logout = async () => {
    setSession(null);
        dispatch({ type: Types.Logout,
      payload: {
        isLoading: false,
      },
    });
  };

  const resetPassword = (email: string) => console.log(email);

  const updateProfile = () => {};

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        logout,
        register,
        setUserInfo,
        resetPassword,
        updateProfile,
        initMsal: () => Promise.resolve(),
        getToken: async () => null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
